import React, { useState, useEffect, useMemo } from 'react';
import {
  FileSpreadsheet, Download, Loader2, AlertTriangle, Boxes, AlertOctagon,
  TrendingDown, Handshake, ClipboardList, FileBarChart2,
} from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import {
  getPurchaseRequests, downloadLagerMaterialsExport, downloadLagerSpecialStockExport, downloadBanfExport,
} from 'core/api/apiService';
import { statusClasses } from '../../core/utils/statusColors';

/**
 * BerichteView — Lager-Berichte: echte BANF-KPIs (aus purchase_requests) +
 * Report-/Export-Hub. Die Berichts-Kacheln lösen 1-Klick-.xlsx über die
 * bestehenden Export-Endpunkte aus (KEIN neues Backend) mit Preset-Filtern.
 */
const BerichteView = ({ user, selectedWerk }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [banfs, setBanfs] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [errKey, setErrKey] = useState(null);
  const [emptyKey, setEmptyKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getPurchaseRequests(user)
      .then((res) => { if (!cancelled) setBanfs(Array.isArray(res) ? res : []); })
      .catch(() => { if (!cancelled) setBanfs([]); });
    return () => { cancelled = true; };
  }, [user]);

  // ---- echte BANF-KPIs ----
  const kpis = useMemo(() => {
    const list = Array.isArray(banfs) ? banfs : [];
    const now = new Date();
    // Lokale Datums-Felder (konsistent zu ProcurementKpiDashboard, grenzwert-sicher)
    let thisMonth = 0;
    let yearValue = 0;
    list.forEach((b) => {
      const d = b.created_at ? new Date(b.created_at) : null;
      if (!d || Number.isNaN(d.getTime())) return;
      if (d.getFullYear() === now.getFullYear()) {
        yearValue += Number(b.total_amount) || 0;
        if (d.getMonth() === now.getMonth()) thisMonth += 1;
      }
    });
    const offen = list.filter((b) => b.status === 'pending').length;
    return { thisMonth, yearValue, offen };
  }, [banfs]);

  const fmtEur = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v ?? 0);

  // ---- Report-Kacheln (Preset-Filter auf bestehende Exporte) ----
  const runExport = async (key, fn) => {
    setBusyKey(key); setErrKey(null); setEmptyKey(null);
    try {
      const r = await fn();
      if (r && r.empty) setEmptyKey(key);
    }
    catch (e) { setErrKey(key); }
    finally { setBusyKey(null); }
  };

  const REPORTS = [
    { key: 'bestand', icon: Boxes, accent: 'green', titleKey: 'lm.repBestand', descKey: 'lm.repBestandDesc',
      run: () => downloadLagerMaterialsExport(user, { werk: selectedWerk, bestandsstatus: 'mit', report: 'bestand' }) },
    { key: 'handlung', icon: AlertTriangle, accent: 'yellow', titleKey: 'lm.repHandlung', descKey: 'lm.repHandlungDesc',
      run: () => downloadLagerMaterialsExport(user, { werk: selectedWerk, handlungsbedarf: true, report: 'handlungsbedarf' }) },
    { key: 'kritisch', icon: AlertOctagon, accent: 'red', titleKey: 'lm.repKritisch', descKey: 'lm.repKritischDesc',
      run: () => downloadLagerMaterialsExport(user, { werk: selectedWerk, kritisch: true, report: 'kritisch' }) },
    { key: 'konsi', icon: Handshake, accent: 'neutral', titleKey: 'lm.repKonsi', descKey: 'lm.repKonsiDesc',
      run: () => downloadLagerSpecialStockExport(user, { werk: selectedWerk }) },
    { key: 'alle', icon: FileSpreadsheet, accent: 'neutral', titleKey: 'lm.repAlle', descKey: 'lm.repAlleDesc',
      run: () => downloadLagerMaterialsExport(user, { werk: selectedWerk, report: 'alle' }) },
    { key: 'banf', icon: ClipboardList, accent: 'neutral', titleKey: 'lm.repBanf', descKey: 'lm.repBanfDesc',
      run: () => {
        const year = new Date().getFullYear();
        const n = (Array.isArray(banfs) ? banfs : []).filter((b) => b.created_at && new Date(b.created_at).getFullYear() === year).length;
        if (n === 0) return Promise.resolve({ empty: true });
        return downloadBanfExport(user, { year });
      } },
  ];

  // ---- Styling ----
  const card = isDarkMode ? 'bg-white/6 border border-white/12 rounded-2xl p-4' : 'bg-white border border-slate-200 rounded-2xl p-4 shadow-sm';
  const labelCls = isDarkMode ? 'text-xs text-white/50 font-medium' : 'text-xs text-slate-500 font-medium';
  const valueCls = isDarkMode ? 'text-2xl font-bold text-white' : 'text-2xl font-bold text-slate-900';
  const mutedCls = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const valCls = isDarkMode ? 'text-white/85' : 'text-slate-800';

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-5">
        <div>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('lm.berichteTitle')}</h2>
          <p className={`text-sm ${mutedCls}`}>{t('lm.berichteSubtitle')}</p>
        </div>

        {/* echte BANF-KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div className={card}><div className={labelCls}>{t('lm.repKpiBanfMonat')}</div><div className={valueCls}>{kpis.thisMonth}</div><div className={`text-xs ${mutedCls}`}>{t('lm.repKpiBanfMonatSub')}</div></div>
          <div className={card}><div className={labelCls}>{t('lm.repKpiWertJahr')}</div><div className={valueCls}>{fmtEur(kpis.yearValue)}</div><div className={`text-xs ${mutedCls}`}>{t('lm.repKpiWertJahrSub')}</div></div>
          <div className={card}><div className={labelCls}>{t('lm.repKpiOffen')}</div><div className={valueCls}>{kpis.offen}</div><div className={`text-xs ${mutedCls}`}>{t('lm.repKpiOffenSub')}</div></div>
        </div>

        {/* Report-Hub */}
        <div>
          <h3 className={`text-sm font-semibold mb-3 ${valCls}`}>{t('lm.repHubTitle')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REPORTS.map((r) => {
              const a = statusClasses(r.accent, isDarkMode);
              const busy = busyKey === r.key;
              const Icon = r.icon;
              return (
                <div key={r.key} className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${a.tint} to-transparent ${isDarkMode ? 'bg-white/[0.04] border border-white/12' : 'bg-white border border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${a.wrap}`}><Icon className="w-5 h-5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-semibold ${valCls}`}>{t(r.titleKey)}</div>
                      <div className={`text-xs ${mutedCls} mt-0.5`}>{t(r.descKey)}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={busy || (!selectedWerk && r.key !== 'banf')}
                    onClick={() => runExport(r.key, r.run)}
                    className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50 ${isDarkMode ? 'bg-brand/80 hover:bg-brand text-white' : 'bg-brand hover:bg-brand/90 text-white'}`}
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    {t('lm.repDownload')}
                  </button>
                  {errKey === r.key && <div className={`mt-1.5 text-[10px] ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('lm.repError')}</div>}
                  {emptyKey === r.key && <div className={`mt-1.5 text-[10px] ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>{t('lm.repEmpty')}</div>}
                </div>
              );
            })}
          </div>
          <div className={`mt-3 flex items-center gap-2 text-[11px] ${mutedCls}`}>
            <FileBarChart2 className="w-3.5 h-3.5" />{t('lm.repHubHint')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BerichteView;
